import React, { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPhoneAlt,
  faEnvelope,
  faMapMarkerAlt,
  faQuestionCircle,
  faChevronDown,
  faChevronUp,
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import { BrandConfig, getBrand } from '../../constants/brands';
import { isStaysSite } from '../../constants/siteConfig';

const Container = styled.div`
  ${tw`min-h-screen bg-bg-secondary py-8 pb-8 md:pb-16 px-4`}
`;

const PageWrapper = styled.div`
  ${tw`w-full max-w-full min-w-0 max-w-screen-xl mx-auto box-border`}
`;

const Header = styled.div`
  ${tw`bg-white rounded-card shadow-card border border-border-default p-8 mb-8 text-center`}
`;

const Title = styled.h1`
  ${tw`text-3xl md:text-4xl font-bold mb-3 text-text-primary`}
`;

const Subtitle = styled.p`
  ${tw`text-lg text-text-secondary`}
`;

const Section = styled.div`
  ${tw`bg-white rounded-card shadow-card border border-border-default p-6 md:p-8 mb-6`}
`;

const SectionTitle = styled.h2`
  ${tw`text-2xl font-bold mb-6 text-text-primary flex items-center`}

  svg {
    ${tw`mr-3 text-text-secondary`}
  }
`;

const FAQItem = styled.div`
  ${tw`mb-4 border-b border-border-default pb-4 last:border-b-0 last:pb-0`}
`;

const FAQQuestion = styled.button`
  ${tw`w-full text-left flex justify-between items-center py-3 px-4 rounded-lg transition-colors`}

  &:hover {
    ${tw`bg-bg-secondary`}
  }

  span {
    ${tw`text-lg font-semibold text-text-primary`}
  }

  svg {
    ${tw`text-text-secondary flex-shrink-0 ml-4`}
  }
`;

const FAQAnswer = styled.div<{ isOpen: boolean }>`
  ${tw`px-4 pb-2 text-text-secondary leading-relaxed overflow-hidden transition-all duration-300 max-w-full min-w-0 box-border`}
  ${({ isOpen }) => (isOpen ? tw`max-h-96 opacity-100` : tw`max-h-0 opacity-0`)}
`;

const ContactInfo = styled.div`
  ${tw`space-y-6`}
`;

const ContactItem = styled.div`
  ${tw`flex items-start`}

  svg {
    ${tw`text-text-secondary text-xl mr-4 mt-1 flex-shrink-0`}
  }
`;

const ContactContent = styled.div`
  ${tw`flex-1`}
`;

const ContactLabel = styled.div`
  ${tw`font-semibold text-text-primary mb-1`}
`;

const ContactText = styled.div`
  ${tw`text-text-secondary`}

  a {
    ${tw`text-text-secondary underline`}

    &:hover {
      ${tw`text-text-primary`}
    }
  }
`;

const HelpArticle = styled.div`
  ${tw`mb-6 p-4 border border-border-default rounded-lg transition-all`}

  &:hover {
    ${tw`border-border-strong shadow-card-hover`}
  }
`;

const HelpArticleTitle = styled.h3`
  ${tw`text-xl font-semibold text-text-primary mb-2`}
`;

const HelpArticleDescription = styled.p`
  ${tw`text-text-secondary mb-3`}
`;

const HelpArticleLink = styled(Link)`
  ${tw`text-text-secondary font-medium inline-flex items-center underline`}

  &:hover {
    ${tw`text-text-primary`}
  }

  &:after {
    content: ' →';
  }
`;

const Button = styled.a`
  ${tw`inline-block bg-black text-white px-6 py-3 rounded-lg font-semibold transition-colors text-center shadow-xs`}

  &:hover {
    ${tw`bg-black-hover`}
  }
`;

function supportContactAnswer(brand: BrandConfig): string {
  const phone = brand.supportPhone ? `via phone at <strong>${brand.supportPhone}</strong>, ` : '';
  return `You can reach our customer support team ${phone}by email at <a href="mailto:${brand.supportEmail}">${brand.supportEmail}</a>, or through the in-app chat feature. Our support team is available to assist you.`;
}

type Faq = { question: string; answer: string };
type HelpArticleItem = { title: string; description: string; link: string };

/** The stays site's questions (spec 016 W26); the answers match its policies and booking rules. */
function stayFaqs(brand: BrandConfig): Faq[] {
  return [
    {
      question: 'How do I book a place to stay?',
      answer:
        'Search by destination, dates and guests, open a place and pick your nights. The booking card shows the full price, with the cleaning fee, service fee and taxes, before you pay. Places with instant booking are confirmed right away; others send the host a request.',
    },
    {
      question: 'What is a booking request?',
      answer:
        'Some hosts approve each stay. Your card is held, not charged, and the host has 24 hours to accept or decline. If the host declines or does not answer, the hold is released and nothing is charged. You can withdraw a request before the host answers.',
    },
    {
      question: 'When do I get the address and check-in details?',
      answer:
        'Listings show an approximate location. The exact address appears on your trip once the stay is confirmed. Check-in details (door code, Wi-Fi) open 48 hours before check-in, and we email them to you.',
    },
    {
      question: 'Can I change my dates or guests?',
      answer:
        'Yes, from your trip page, when the new nights are free. Nights you keep keep their booked price. A higher total is charged to your card; a lower one is refunded under the cancellation policy. Once your stay has started you can stay longer or add guests.',
    },
    {
      question: 'What is your cancellation policy?',
      answer:
        'Each host chooses Flexible, Moderate, Firm, Strict or Non-refundable. The listing and your trip page show which one applies and exactly what you get back. See the <a href="/policies/cancellation">Cancellation Policy</a>. If the host cancels, you get a full refund.',
    },
    {
      question: 'How do reviews work?',
      answer:
        'After checkout, you and your host can each write a review for 14 days. Reviews show once you both have written one, or when the 14 days end, so neither side sees the other first.',
    },
    {
      question: 'How do I become a host?',
      answer:
        'Apply from your profile and accept the Host Agreement. Once you are approved, create a listing: describe the place, add photos, set prices and house rules, choose a cancellation policy and publish.',
    },
    {
      question: 'What payment methods do you accept?',
      answer:
        'Major credit and debit cards in your own name. Payments are processed securely by Stripe, and you can save cards on the Payment methods page.',
    },
    {
      question: 'How do I contact customer support?',
      answer: supportContactAnswer(brand),
    },
  ];
}

function stayHelpArticles(brand: BrandConfig): HelpArticleItem[] {
  return [
    {
      title: 'Find a place to stay',
      description: `Search homes, cabins and apartments on ${brand.name} and book your first stay.`,
      link: '/stays/search',
    },
    {
      title: 'Hosting your place',
      description: 'Create a listing, set prices and house rules, and manage reservations.',
      link: '/stays/host/listings',
    },
    {
      title: 'Cancellation Policy',
      description: 'The five cancellation policies, refunds, and changing a reservation.',
      link: '/policies/cancellation',
    },
    {
      title: 'Host Agreement',
      description: 'What hosts agree to: listings, fees, cancellations, payouts and safety.',
      link: '/policies/host-agreement',
    },
    {
      title: 'Terms of Service',
      description: 'Your rights and responsibilities as a guest or host.',
      link: '/policies/terms',
    },
    {
      title: 'Privacy Policy',
      description: 'How we collect, use, and protect your personal information.',
      link: '/policies/privacy',
    },
  ];
}

const HelpPage: React.FC = () => {
  const brand = getBrand();
  const stays = isStaysSite();
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const carFaqs: Faq[] = [
    {
      question: 'How do I book a car?',
      answer:
        'To book a car, simply browse available vehicles on our platform, select your desired dates, and complete the booking process. You can filter cars by location, price, and features to find the perfect vehicle for your needs.',
    },
    {
      question: 'What are the requirements to rent a car?',
      answer:
        "You must be at least 18 years old (or 25 for certain premium vehicles), have a valid driver's license, and meet our protection plan requirements. Additional requirements may apply for specific vehicles.",
    },
    {
      question: 'How do I become a host?',
      answer:
        'To become a host, create an account and navigate to the "Host" section. You can list your vehicle by providing details, photos, pricing, and availability. Our team will review your listing before it goes live.',
    },
    {
      question: 'What payment methods do you accept?',
      answer:
        'We accept major credit cards (Visa, MasterCard, American Express) and debit cards. All payments are processed securely through our payment platform.',
    },
    {
      question: 'What is your cancellation policy?',
      answer:
        'Cancellation policies vary by booking. Please refer to the specific cancellation policy for each booking, which you can find in your booking details or our <a href="/policies/cancellation">Cancellation Policy</a> page.',
    },
    {
      question: "What happens if there's damage to the vehicle?",
      answer:
        "All bookings include protection plans. In case of damage, please report it immediately through the app or contact support. We'll guide you through the claims process.",
    },
    {
      question: 'How do I contact customer support?',
      answer: supportContactAnswer(brand),
    },
  ];

  const carHelpArticles: HelpArticleItem[] = [
    {
      title: 'Getting Started Guide',
      description:
        'Learn how to create an account, browse vehicles, and make your first booking on Autopilotee Cars.',
      link: '/',
    },
    {
      title: 'Hosting Your Vehicle',
      description:
        'Complete guide on how to list your vehicle, set pricing, manage bookings, and maximize your earnings.',
      link: '/cars/host',
    },
    {
      title: 'Cancellation Policy',
      description:
        'Understand our cancellation policy, refund procedures, and how to modify or cancel your booking.',
      link: '/policies/cancellation',
    },
    {
      title: 'Terms of Service',
      description:
        'Read our terms of service to understand your rights and responsibilities when using our platform.',
      link: '/policies/terms',
    },
    {
      title: 'Privacy Policy',
      description: 'Learn how we collect, use, and protect your personal information.',
      link: '/policies/privacy',
    },
  ];

  const faqs = stays ? stayFaqs(brand) : carFaqs;
  const helpArticles = stays ? stayHelpArticles(brand) : carHelpArticles;

  return (
    <Container>
      <PageWrapper>
        <Header>
          <Title>Help & Support</Title>
          <Subtitle>
            Find answers to common questions and get in touch with our support team
          </Subtitle>
        </Header>

        <Section>
          <SectionTitle>
            <FontAwesomeIcon icon={faQuestionCircle} />
            Frequently Asked Questions
          </SectionTitle>
          {faqs.map((faq, index) => (
            <FAQItem key={index}>
              <FAQQuestion onClick={() => toggleFAQ(index)}>
                <span>{faq.question}</span>
                <FontAwesomeIcon icon={openFAQ === index ? faChevronUp : faChevronDown} />
              </FAQQuestion>
              <FAQAnswer isOpen={openFAQ === index}>
                <div dangerouslySetInnerHTML={{ __html: faq.answer }} />
              </FAQAnswer>
            </FAQItem>
          ))}
        </Section>

        <Section>
          <SectionTitle>Contact Information</SectionTitle>
          <ContactInfo>
            <ContactItem>
              <FontAwesomeIcon icon={faPhoneAlt} />
              <ContactContent>
                <ContactLabel>{brand.supportPhone ? 'Phone Support' : 'Support'}</ContactLabel>
                <ContactText>
                  {brand.supportPhone ? (
                    <a href={`tel:${brand.supportPhone.replace(/[^+\d]/g, '')}`}>
                      <strong>{brand.supportPhone}</strong>
                    </a>
                  ) : (
                    <a href={`mailto:${brand.supportEmail}`}>
                      <strong>{brand.supportEmail}</strong>
                    </a>
                  )}
                  <br />
                </ContactText>
              </ContactContent>
            </ContactItem>

            <ContactItem>
              <FontAwesomeIcon icon={faEnvelope} />
              <ContactContent>
                <ContactLabel>Email Support</ContactLabel>
                <ContactText>
                  <a href={`mailto:${brand.supportEmail}`}>{brand.supportEmail}</a>
                  <br />
                  We typically respond within 24 hours
                </ContactText>
              </ContactContent>
            </ContactItem>

            <ContactItem>
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              <ContactContent>
                <ContactLabel>Business Address</ContactLabel>
                <ContactText>
                  2150 N first Street STE 426
                  <br />
                  San Jose, CA 95131
                  <br />
                  United States
                </ContactText>
              </ContactContent>
            </ContactItem>
          </ContactInfo>

          {brand.supportPhone ? (
            <div style={{ marginTop: '2rem', textAlign: 'center' }}>
              <Button href={`tel:+1${brand.supportPhone.replace(/\D/g, '').slice(-10)}`}>
                Call Support Now
              </Button>
            </div>
          ) : null}
        </Section>

        <Section>
          <SectionTitle>Help Articles</SectionTitle>
          {helpArticles.map((article, index) => (
            <HelpArticle key={index}>
              <HelpArticleTitle>{article.title}</HelpArticleTitle>
              <HelpArticleDescription>{article.description}</HelpArticleDescription>
              <HelpArticleLink to={article.link}>Read More</HelpArticleLink>
            </HelpArticle>
          ))}
        </Section>

        <Section>
          <SectionTitle>Customer Service Options</SectionTitle>
          <ContactInfo>
            {/* <ContactItem>
              <ContactContent>
                <ContactLabel>In-App Chat</ContactLabel>
                <ContactText>
                  Use the chat feature in the Autopilotee Cars app for quick
                  assistance. Available 24/7.
                </ContactText>
              </ContactContent>
            </ContactItem> */}

            <ContactItem>
              {brand.supportPhone && (
                <ContactContent>
                  <ContactLabel>Phone Support</ContactLabel>
                  <ContactText>
                    Call us at <strong>{brand.supportPhone}</strong> for immediate assistance with
                    bookings, payments, or emergencies.
                  </ContactText>
                </ContactContent>
              )}
            </ContactItem>

            <ContactItem>
              <ContactContent>
                <ContactLabel>Email Support</ContactLabel>
                <ContactText>
                  Send detailed inquiries to{' '}
                  <a href={`mailto:${brand.supportEmail}`}>{brand.supportEmail}</a>. Include your
                  booking reference number for faster service.
                </ContactText>
              </ContactContent>
            </ContactItem>

            <ContactItem>
              {/* <ContactContent>
                <ContactLabel>Roadside Assistance</ContactLabel>
                <ContactText>
                  For vehicle emergencies during your booking, contact our 24/7
                  roadside assistance at <strong>+1(866)217-2849</strong>.
                </ContactText>
              </ContactContent> */}
            </ContactItem>
          </ContactInfo>
        </Section>
      </PageWrapper>
    </Container>
  );
};

export default HelpPage;
